import type { Maybe } from '@adornis/base/utilTypes.js';
import { guardDialog, showDialog } from '@adornis/dialog/dialog-functions.js';
import { EntityPickerInput } from '@adornis/entity-picker/client/entity-picker-input.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { DialogHeader } from '@adornis/components/client/DialogHeader.js';
import { html } from 'lit';
import { SgSnackbar } from '../../client/components/ui/sg-snackbar.js';
import { Person } from '../../users/entities/person.js';
import { UserListAPI } from '../../users/list/api/UserListAPI.js';
import { copyUserBasedViewProfileToUser } from '../copyUserBasedViewProfileToUser.js';

const ShareViewProfileDialog = stateful(
  (props: { viewID: string; resolve: (value: unknown) => void; reject: () => void }) => {
    const [recipientID, setRecipientID] = useState<Maybe<string>>(null);
    // a filter rebuilt per render carries a fresh _id and refetches the picker
    const [filter] = useState(new AdornisFilter({}));

    const handleAccept = async () => {
      if (!recipientID) return;
      await guardDialog(() => copyUserBasedViewProfileToUser(props.viewID, recipientID)(), {
        success: () => {
          props.resolve(null);
          void SgSnackbar.show('Ansicht weitergegeben');
        },
      });
    };

    return html`
      <div class="dialog-container">
        ${DialogHeader({ title: 'Ansicht weitergeben', close: props.reject, closeLabel: 'Dialog schließen' })}

        <p>
          Die ausgewählte Person erhält eine Kopie der aktuellen Ansicht mit allen Filtern und Spalten. Spätere
          Änderungen wirken sich nicht auf die jeweils andere Ansicht aus.
        </p>

        ${EntityPickerInput({
          value: recipientID,
          class: Person,
          operation: UserListAPI.subscribe,
          filter,
          placeholder: 'Person',
          placeholderMode: 'hide',
          renderString: entity =>
            `${entity.profilDaten?.stammdaten?.vorname ?? ''} ${entity.profilDaten?.stammdaten?.nachname ?? ''} (${entity.email ?? ''})`,
          selectionSet: { _id: 1, email: 1, profilDaten: { stammdaten: { vorname: 1, nachname: 1 } } },
          onPicked: value => setRecipientID(value),
        })}

        <button class="btn btn-primary" ?disabled=${!recipientID} @click=${handleAccept}>Weitergeben</button>
      </div>
    `;
  },
);

export const openShareViewProfileDialog = (viewID: string) =>
  showDialog({
    content: (resolve, reject) => ShareViewProfileDialog({ viewID, resolve, reject }),
    modalBoxClasses: 'max-w-2xl',
  }).catch(() => {});
